import {
	type LoaderFunctionArgs,
	type MetaFunction,
	redirect,
} from '@remix-run/node'
import { Link } from '@remix-run/react'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getEffectiveDisabledRoles } from '#app/utils/permissions.server.ts'
import { EndCap } from './components/end-cap.tsx'
import { FeatureRow } from './components/feature-row.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'
import { MarketingSection } from './components/marketing-section.tsx'
import { NotationPreview } from './components/notation-preview.tsx'

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const userId = await getUserId(request)
	if (userId) {
		const disabledRoles = await getEffectiveDisabledRoles(request, userId)
		if (disabledRoles.length > 0) {
			// A restriction is active - land on the highest enabled role's home
			const user = await prisma.user.findFirst({
				where: { id: userId },
				select: { roles: { select: { name: true } } },
			})
			const enabled = (user?.roles ?? [])
				.map((role) => role.name)
				.filter((role) => !disabledRoles.includes(role))
			if (enabled.includes('admin')) throw redirect('/admin')
			if (enabled.includes('coach')) throw redirect('/coaching')
		}
		throw redirect('/dashboard')
	}
	return null
}

export const meta: MetaFunction = () => [
	{ title: 'Momentum — for the coach-athlete pair' },
	{
		name: 'description',
		content:
			'Coach and athlete write training in the same notation. Programs become readable, comparable, compoundable.',
	},
]

export default function PublicLandingRoute() {
	return (
		<>
			<MarketingHero
				eyebrow="Momentum · the language of training"
				heading="Build Momentum."
				subhead="Built for coaches and athletes who want clear and trackable training, communicated effectively."
				decorations={['grid']}
			/>

			<MarketingSection>
				<EyebrowLabel>One language</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					Coach writes the program. Athlete logs the work.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Programs become readable, comparable, sharable. No translation between
					what the coach prescribed and what the athlete did.
				</p>
				<div>
					<EyebrowLabel className="mt-8">Coach Assigns</EyebrowLabel>
					<NotationPreview>Bench Press, 3x5 @8rpe every 2:30</NotationPreview>
				</div>
				<div>
					<EyebrowLabel className="mt-8">Athlete Logs</EyebrowLabel>
					<NotationPreview>
						Bench Press, 3x5 @[155..3,145..2]lb @[8,8,9,8,8]rpe every 2:30
					</NotationPreview>
				</div>
				<div className="mt-6">
					<Link to="/how-to" className="ember-link text-sm">
						See how the grammar works →
					</Link>
				</div>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>What stacks</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					Three things that compound.
				</h2>
				<FeatureRow
					className="mt-12"
					items={[
						{
							heading: 'Reps',
							body: 'Every set logged adds planning context; solo, or across a roster.',
						},
						{
							heading: 'Programs',
							body: 'Phases and microcycles in absolute or relative intensity and progression notation. One athlete, or fifty.',
						},
						{
							heading: 'Trust',
							body: 'Visibility between coach and athlete. Adherence across a team.',
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection tone="cream">
				<EyebrowLabel tone="cream">
					Write one workout, and scale it over many cycles.
				</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					Coaches scale their craft. Athletes train with structure.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-cream-fg-soft">
					Using the Cycles feature, coaches can write a workout once and repeat
					it with planned adjustments over time. Get a clear roadmap of their
					training, with the ability to track progress and stay motivated.
				</p>
				<div className="mt-8">
					<Link
						to="/coach"
						className="ember-link text-sm"
						style={{ color: 'var(--m-cream-ember)' }}
					>
						Coaches →
					</Link>
				</div>
			</MarketingSection>

			<EndCap />
		</>
	)
}
